import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {ServerEndpoints} from '../../../../server.endpoints';
import {FiscalJobDetail} from '../../_models/fiscal-jobs/fiscal-job.models';
import {
  ClosingCorrectionCandidate,
  ClosingCorrectionCandidateQuery,
  ClosingCorrectionCreateRequest
} from '../../_models/closing-corrections/closing-correction.models';

/**
 * The typed HTTP surface for correcting filed cash point closings (#771). Two calls, no state.
 *
 * Everything else the screen needs already exists: the register filter is the data export's
 * `/fiscal/exports/Registers`, and a started run is read, pushed and retried through the fiscal job engine
 * (`FiscalJobStore`). A second list of runs here would be a second copy of state the engine owns.
 */
@Injectable({providedIn: 'root'})
export class ClosingCorrectionHttpService {

  // `BASE_URL` is a string DI token, so it comes in through @Inject, like the other http services here.
  constructor(@Inject('BASE_URL') private readonly baseUrl: string, private readonly http: HttpClient) {
  }

  /**
   * The filed closings in a period that may be corrected.
   *
   * The registers go out as a repeated `registerIds` parameter, which is how ASP.NET binds a list from a
   * query string. None means every register.
   *
   * Two fields are normalised at this boundary because the monolith leaves null and default values out of
   * its JSON: an unnamed register arrives without `registerName`, and without the `=== true` a missing
   * `correctionOutstanding` would be read as "not outstanding" only by accident.
   */
  candidates(query: ClosingCorrectionCandidateQuery): Observable<ClosingCorrectionCandidate[]> {
    let params = new HttpParams().set('from', query.from).set('to', query.to);
    for (const id of query.registerIds) params = params.append('registerIds', id);

    return this.http
      .get<Partial<ClosingCorrectionCandidate>[]>(
        `${this.baseUrl}${ServerEndpoints.Fiscal_ClosingCorrections_Candidates}`, {params})
      .pipe(map(rows => rows.map(row => ({
        entryId: row.entryId ?? '',
        registerId: row.registerId ?? '',
        registerName: row.registerName ?? null,
        closingNumber: row.closingNumber ?? 0,
        businessDate: row.businessDate ?? '',
        localState: row.localState === 'completed' ? 'completed' : 'transmitted',
        correctionOutstanding: row.correctionOutstanding === true,
        filings: row.filings ?? 0
      }))));
  }

  /**
   * Starts a correction run over the chosen closings.
   *
   * Answers as soon as the run is durable and scheduled. The withdrawals and new filings happen afterwards,
   * one entry per closing, and are followed through the fiscal job store.
   */
  create(request: ClosingCorrectionCreateRequest): Observable<FiscalJobDetail> {
    return this.http.post<FiscalJobDetail>(
      `${this.baseUrl}${ServerEndpoints.Fiscal_ClosingCorrections_Create}`, request);
  }
}
